import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PaymentReportItem {
  id: string;
  payment_date: string;
  invoice_number: string;
  patient_name: string;
  amount: number;
  payment_method: string;
  reference_number?: string;
  status: string;
}

export default function PaymentReports() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<PaymentReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');

  useEffect(() => {
    loadPaymentReports();
  }, [startDate, endDate, paymentMethodFilter]);

  const loadPaymentReports = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockPaymentReports());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('payments')
        .select(`
          id,
          payment_date,
          amount,
          payment_method,
          reference_number,
          invoice:invoices(invoice_number, status, patient:patients(name))
        `);

      if (paymentMethodFilter !== 'all') {
        query = query.eq('payment_method', paymentMethodFilter);
      }
      if (startDate) {
        query = query.gte('payment_date', startDate);
      }
      if (endDate) {
        query = query.lte('payment_date', endDate);
      }

      const { data, error } = await query.order('payment_date', { ascending: false });
      if (error) throw error;

      const formattedData: PaymentReportItem[] = (data || []).map(item => ({
        id: item.id,
        payment_date: item.payment_date,
        invoice_number: item.invoice?.invoice_number || 'N/A',
        patient_name: item.invoice?.patient?.name || 'N/A',
        amount: item.amount,
        payment_method: item.payment_method,
        reference_number: item.reference_number,
        status: item.invoice?.status || 'N/A',
      }));

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading payment reports:', error);
      setReportData(getMockPaymentReports());
    } finally {
      setLoading(false);
    }
  };

  const getMockPaymentReports = (): PaymentReportItem[] => {
    return [
      {
        id: '1',
        payment_date: '2025-01-27',
        invoice_number: 'INV-2025-001',
        patient_name: 'John Doe',
        amount: 5000,
        payment_method: 'cash',
        reference_number: 'CASH-001',
        status: 'paid',
      },
      {
        id: '2',
        payment_date: '2025-01-27',
        invoice_number: 'INV-2025-002',
        patient_name: 'Jane Smith',
        amount: 3000,
        payment_method: 'card',
        reference_number: 'CARD-002',
        status: 'paid',
      },
      {
        id: '3',
        payment_date: '2025-01-26',
        invoice_number: 'INV-2025-003',
        patient_name: 'Ahmed Khan',
        amount: 7500,
        payment_method: 'bank_transfer',
        reference_number: 'BANK-003',
        status: 'paid',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.reference_number && item.reference_number.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const totalPayments = filteredData.reduce((sum, item) => sum + item.amount, 0);
  const cashPayments = filteredData.filter(item => item.payment_method === 'cash').reduce((sum, item) => sum + item.amount, 0);
  const cardPayments = filteredData.filter(item => item.payment_method === 'card').reduce((sum, item) => sum + item.amount, 0);

  const handleExportCSV = () => {
    const headers = ['Date', 'Invoice #', 'Patient', 'Amount', 'Payment Method', 'Reference #', 'Status'];
    const rows = filteredData.map(item => [
      item.payment_date,
      item.invoice_number,
      item.patient_name,
      item.amount,
      item.payment_method,
      item.reference_number || 'N/A',
      item.status,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Payment Reports</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Payments</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalPayments)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Cash Payments</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(cashPayments)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Card Payments</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">{formatCurrency(cardPayments)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search invoice, patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Methods</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="mobile_banking">Mobile Banking</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.payment_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.patient_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(item.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.payment_method === 'cash' ? 'bg-green-100 text-green-800' :
                          item.payment_method === 'card' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {item.payment_method}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.reference_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
